<template>
  <el-dialog :title="dialogTitle" :visible.sync="visible" width="min(1100px, calc(100vw - 24px))" :close-on-click-modal="false" append-to-body>
    <el-alert type="info" :closable="false" show-icon :title="alertTitle" style="margin-bottom:12px" />

    <div v-if="fromSalesOrderList && presetOrderSummaries.length" style="margin-bottom:12px">
      <el-tag v-for="item in presetOrderSummaries" :key="item.salesOrderId" size="small" style="margin:0 8px 8px 0">
        {{ item.salesOrderCode }} / {{ item.customerName || '—' }}
      </el-tag>
    </div>

    <el-form :inline="true" :model="query" size="small" @keyup.enter.native="search">
      <el-form-item label="成品">
        <el-input v-model="query.materialCode" readonly placeholder="可选成品" style="width:160px">
          <el-button slot="append" @click="materialSelectorVisible = true">选择</el-button>
        </el-input>
      </el-form-item>
      <el-form-item v-if="!fromSalesOrderList" label="销售单号">
        <el-input v-model="query.salesOrderCode" clearable style="width:130px" />
      </el-form-item>
      <el-form-item v-if="!fromSalesOrderList" label="客户">
        <el-input v-model="query.customerName" clearable style="width:130px" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">查询</el-button>
        <el-button @click="resetQuery">重置</el-button>
      </el-form-item>
      <el-form-item label="BOM版本">
        <el-select v-model="bomVersion" clearable filterable placeholder="不选则下达时取当前生效版" style="width:220px" :disabled="submitting || !bomVersionOptions.length">
          <el-option v-for="v in bomVersionOptions" :key="v" :label="v" :value="v" />
        </el-select>
        <span v-if="selectedMaterialId && !bomVersionOptions.length" style="margin-left:8px;color:#909399">请先勾选明细</span>
      </el-form-item>
    </el-form>

    <el-table
      ref="lineTable"
      :data="displayLines"
      row-key="salesOrderLineId"
      border
      v-loading="loading"
      max-height="360"
      :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
      @selection-change="onSelectionChange"
    >
      <el-table-column type="selection" :reserve-selection="true" width="45" header-align="center" align="center" />
      <el-table-column prop="salesOrderCode" label="销售单号" min-width="120" header-align="center" align="left" />
      <el-table-column prop="customerName" label="客户" min-width="120" header-align="center" align="left" show-overflow-tooltip />
      <el-table-column prop="materialCode" label="物料编码" min-width="110" header-align="center" align="left" />
      <el-table-column prop="materialName" label="物料名称" min-width="130" header-align="center" align="left" show-overflow-tooltip />
      <el-table-column prop="quantity" label="订单数量" width="95" header-align="center" align="left" />
      <el-table-column prop="remainProductionQty" label="可生产量" width="95" header-align="center" align="left" />
      <el-table-column prop="deliveryDate" label="交货日期" width="105" header-align="center" align="left">
        <template slot-scope="scope">{{ (scope.row.deliveryDate || '').slice(0, 10) }}</template>
      </el-table-column>
      <el-table-column label="本次生产" width="180" header-align="center" align="left" fixed="right">
        <template slot-scope="scope">
          <el-input-number
            v-model="scope.row.pushQty"
            :min="0"
            :max="Number(scope.row.remainProductionQty || 0)"
            :precision="2"
            size="small"
            style="width:100%"
          />
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="!fromSalesOrderList"
      style="margin-top:12px;text-align:right"
      @current-change="val => { pageIndex = val; load() }"
      @size-change="val => { pageSize = val; pageIndex = 1; load() }"
      :current-page="pageIndex"
      :page-sizes="[20, 50, 100]"
      :page-size="pageSize"
      :total="total"
      layout="total, sizes, prev, pager, next"
    />
    <div v-else style="margin-top:12px;text-align:right;color:#909399;font-size:13px">
      共 {{ displayLines.length }} 条可下推明细
    </div>

    <div v-if="selected.length" style="margin-top:8px;color:#606266;font-size:13px">
      已选 {{ selected.length }} 行，合计生产 {{ totalPushQty }}；涉及销售单 {{ distinctOrderCount }} 张
    </div>

    <span slot="footer">
      <el-button @click="visible = false">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="submit">合并下推</el-button>
    </span>

    <material-selector :visible.sync="materialSelectorVisible" :multiple="false" @select="onMaterialSelect" />
  </el-dialog>
</template>

<script>
import MaterialSelector from '../../components/MaterialSelector.vue'
import { listLinesForMergeProduction, getLinesForProduction, pushFromSales, listMaterialBomVersion } from '../../api/production/productionOrder'

export default {
  name: 'ProductionPushMergeFromSales',
  components: { MaterialSelector },
  data() {
    return {
      visible: false,
      loading: false,
      submitting: false,
      materialSelectorVisible: false,
      query: { materialId: '', materialCode: '', salesOrderCode: '', customerName: '' },
      bomVersion: '',
      bomVersionOptions: [],
      lines: [],
      selected: [],
      pushQtyByLine: {},
      pageIndex: 1,
      pageSize: 50,
      total: 0,
      presetSalesOrderIds: [],
      presetOrderSummaries: [],
      fromSalesOrderList: false
    }
  },
  computed: {
    dialogTitle() {
      return this.fromSalesOrderList ? '销售订单合并下推生产' : '跨订单合并下推生产'
    },
    alertTitle() {
      if (this.fromSalesOrderList && this.presetSalesOrderIds.length) {
        return `已限定 ${this.presetSalesOrderIds.length} 张销售订单；请勾选同一成品的明细行，合并生成一张生产工单。`
      }
      return '勾选同一成品的多个销售订单行，合并生成一张生产工单；须为已确认订单且可生产量大于 0。'
    },
    displayLines() {
      if (!this.fromSalesOrderList) return this.lines
      return this.applyClientFilters(this.lines)
    },
    selectedMaterialId() {
      if (!this.selected.length) return this.query.materialId || ''
      return this.selected[0].materialId
    },
    totalPushQty() {
      return this.selected.reduce((sum, row) => sum + Number(row.pushQty || 0), 0)
    },
    distinctOrderCount() {
      return new Set(this.selected.map(r => r.salesOrderId).filter(Boolean)).size
    }
  },
  methods: {
    init(options) {
      const opts = options || {}
      this.visible = true
      this.pageIndex = 1
      this.bomVersion = ''
      this.bomVersionOptions = []
      this.selected = []
      this.pushQtyByLine = {}
      this.lines = []
      this.presetSalesOrderIds = Array.isArray(opts.salesOrderIds) ? opts.salesOrderIds.filter(Boolean) : []
      this.presetOrderSummaries = Array.isArray(opts.salesOrders)
        ? opts.salesOrders.filter(o => o && o.salesOrderId).map(o => ({
          salesOrderId: o.salesOrderId,
          salesOrderCode: o.salesOrderCode,
          customerName: o.customerName
        }))
        : []
      this.fromSalesOrderList = this.presetSalesOrderIds.length > 0
      this.$nextTick(() => this.$refs.lineTable && this.$refs.lineTable.clearSelection())
      this.resetQuery(false)
      this.load()
    },
    clearSelection() {
      this.selected = []
      this.pushQtyByLine = {}
      if (this.$refs.lineTable) this.$refs.lineTable.clearSelection()
    },
    rememberQuantities() {
      this.lines.forEach(row => {
        if (row.salesOrderLineId) this.pushQtyByLine[row.salesOrderLineId] = Number(row.pushQty || 0)
      })
    },
    resetQuery(reload = true) {
      this.query = { materialId: '', materialCode: '', salesOrderCode: '', customerName: '' }
      this.pageIndex = 1
      if (reload) { this.clearSelection(); this.load() }
    },
    onMaterialSelect(row) {
      if (!row) return
      this.clearSelection()
      this.query.materialId = row.materialId
      this.query.materialCode = row.materialCode
      this.pageIndex = 1
      if (!this.fromSalesOrderList) this.load()
    },
    search() {
      this.clearSelection()
      this.pageIndex = 1
      this.load()
    },
    applyClientFilters(rows) {
      let list = rows || []
      if (this.query.materialId) list = list.filter(r => r.materialId === this.query.materialId)
      return list
    },
    buildOrderMap() {
      const map = {}
      this.presetOrderSummaries.forEach(o => { map[o.salesOrderId] = o })
      return map
    },
    loadFromSelectedOrders() {
      const orderMap = this.buildOrderMap()
      const requests = this.presetSalesOrderIds.map(id =>
        getLinesForProduction(id).then(res => {
          const list = Array.isArray(res.data) ? res.data : ((res.data && res.data.list) || [])
          return (list || []).map(line => {
            const order = orderMap[line.salesOrderId] || orderMap[id] || {}
            return {
              ...line,
              salesOrderId: line.salesOrderId || id,
              salesOrderCode: line.salesOrderCode || order.salesOrderCode,
              customerName: line.customerName || order.customerName,
              pushQty: Number(line.remainProductionQty || 0)
            }
          })
        })
      )
      return Promise.all(requests).then(groups => {
        this.lines = groups.reduce((acc, cur) => acc.concat(cur), [])
        this.total = this.lines.length
      })
    },
    load() {
      this.rememberQuantities()
      this.loading = true
      if (this.fromSalesOrderList) {
        this.loadFromSelectedOrders().then(() => {
          this.loading = false
          if (!this.lines.length) this.$message.warning('所选销售订单没有可下推生产的明细')
        }).catch(() => {
          this.loading = false
          this.$message.error('加载失败')
        })
        return
      }
      listLinesForMergeProduction({
        currentPage: this.pageIndex,
        pageSize: this.pageSize,
        materialId: this.query.materialId || undefined,
        materialCode: this.query.materialCode || undefined,
        salesOrderCode: this.query.salesOrderCode || undefined,
        customerName: this.query.customerName || undefined
      }).then(res => {
        this.loading = false
        const body = res.data || {}
        this.lines = (body.list || []).map(l => ({
          ...l,
          pushQty: Object.prototype.hasOwnProperty.call(this.pushQtyByLine, l.salesOrderLineId)
            ? this.pushQtyByLine[l.salesOrderLineId] : Number(l.remainProductionQty || 0)
        }))
        this.total = body.totalCount || 0
      }).catch(() => {
        this.loading = false
        this.lines = []
        this.total = 0
        this.$message.error('加载失败')
      })
    },
    onSelectionChange(rows) {
      this.selected = rows || []
      this.loadBomVersions()
    },
    loadBomVersions() {
      const mid = this.selectedMaterialId
      if (!mid) {
        this.bomVersionOptions = []
        return
      }
      if (this.selected.length && this.selected.some(r => r.materialId !== mid)) {
        this.bomVersionOptions = []
        return
      }
      listMaterialBomVersion(mid).then(res => {
        this.bomVersionOptions = res.data || []
        if (this.bomVersion && !this.bomVersionOptions.includes(this.bomVersion)) this.bomVersion = ''
      }).catch(() => { this.bomVersionOptions = [] })
    },
    submit() {
      if (!this.selected.length) {
        this.$message.warning('请勾选销售明细')
        return
      }
      const mid = this.selected[0].materialId
      if (this.selected.some(r => r.materialId !== mid)) {
        this.$message.warning('合并生产须为同一物料')
        return
      }
      if (this.selected.length < 2 && this.distinctOrderCount < 2) {
        this.$confirm('仅选择了单行明细，仍合并下推？（单订单内多行或跨订单合并更有价值）', '提示', { type: 'warning' })
          .then(() => this.doPush())
          .catch(() => {})
        return
      }
      this.doPush()
    },
    doPush() {
      const payloadLines = []
      for (const row of this.selected) {
        const qty = Number(row.pushQty || 0)
        if (qty <= 0) {
          this.$message.warning('本次生产数量必须大于0')
          return
        }
        payloadLines.push({ salesOrderLineId: row.salesOrderLineId, quantity: qty })
      }
      this.submitting = true
      const payload = { mergeMode: 'merge', lines: payloadLines }
      if (this.bomVersion) payload.bomVersion = this.bomVersion
      pushFromSales(payload).then(res => {
        this.submitting = false
        const ids = (res.data && res.data.productionOrderIds) || (res.data && res.data.list) || res.data || []
        const n = Array.isArray(ids) ? ids.length : 0
        this.$message.success('已生成 ' + n + ' 张生产工单')
        this.visible = false
        this.$emit('success')
      }).catch(() => { this.submitting = false })
    }
  }
}
</script>
